import { useParams } from 'react-router-dom'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { DataTable } from 'components/Shared/DataTable'
import { type GridColDef } from '@mui/x-data-grid'
import { colorPrimitives } from 'components/Theme'
import { ErrorIndicator } from 'components/ErrorIndicator/ErrorIndicator'
import { useGetPatronSweepstakesById } from 'hooks/api/Patron/useGetPatronSweepstakesById'
import { PatronDetailsHeader } from '../DetailsHeader/PatronDetailsHeader'
import { useGetPatronById } from 'hooks/api/Patron/useGetPatronById'
import { Box, Stack, Typography } from '@mui/material'
import { dataTablePromotionDateFormatter } from 'utils/util'
import { type SweepstakeWinnerStatusType } from 'types/api'

interface PatronSweepstake {
  id: string
  sweepstakeId: number
  name: string
  startDate: string
  endDate: string
  prizeLocation: string
  tierType: string
  status: string
  prizeDescription: string
}

export const PatronSweepstakesPage = () => {
  const { id: patronId } = useParams()
  const patronQuery = useGetPatronById(Number(patronId))
  const patronSweepstakesQuery = useGetPatronSweepstakesById({
    patronId: Number(patronId),
  })

  if (
    patronQuery.isPending ||
    patronSweepstakesQuery.isPending ||
    !patronQuery.data
  ) {
    return <ActivityIndicator />
  }

  if (patronQuery.isError || patronSweepstakesQuery.isError) {
    return <ErrorIndicator message="An Error Occurred" />
  }

  const statusText: Record<SweepstakeWinnerStatusType, string> = {
    LocationValidation: 'Location Validation',
    RedrawRequest: 'Redraw Requested',
    WinnerValidation: 'Winner Validated',
    WinnerNotified: 'Winner Notified',
    AffidavitReceived: 'Affidavit Received',
    SentToAP: 'Sent To AP',
    Awarded: 'Awarded',
    NoWinner: 'No Winner',
    GrandPrizeValidation: 'Grand Prize Validation',
  }

  const patronSweepstakeColumns: Array<GridColDef<PatronSweepstake>> = [
    {
      field: 'sweepstakeId',
      headerName: 'Id',
      minWidth: 100,
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 300,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      minWidth: 175,
      flex: 1,
      valueFormatter: dataTablePromotionDateFormatter(false, 'Start'),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      minWidth: 175,
      flex: 1,
      valueFormatter: dataTablePromotionDateFormatter(false, 'End'),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 200,
      valueFormatter: (params) => {
        const status = params.value as SweepstakeWinnerStatusType
        return statusText[status] || status
      },
    },
    {
      field: 'tierType',
      headerName: 'Tier',
      minWidth: 100,
    },
    {
      field: 'prizeLocation',
      headerName: 'Prize Location',
      minWidth: 200,
    },
    {
      field: 'prizeDescription',
      headerName: 'Prize Description',
      minWidth: 250,
    },
  ]

  const patronSweepstakeRows: PatronSweepstake[] = (
    patronSweepstakesQuery.data || []
  ).map((row, index) => ({
    // Use a unique row id for rendering; sweepstake ids can repeat for a patron.
    id: `${String(row.id ?? 0)}-${String(index)}`,
    sweepstakeId: row.id ?? 0,
    name: row.name ?? '',
    startDate: row.startDate ?? '',
    endDate: row.endDate ?? '',
    prizeLocation: row.prizeLocation ?? '',
    tierType: row.tierType ?? '',
    status: row.status ?? '',
    prizeDescription: row.prizeDescription ?? '',
  }))

  return (
    <PatronDetailsHeader
      patron={patronQuery.data}
      currentTab={`/PatronAccounts/${patronId}/Sweepstakes`}
    >
      <Box paddingBottom={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingBottom={3}
        >
          <Typography variant={'h3'}>Sweepstakes</Typography>
        </Stack>
        {patronQuery.isPending ? (
          <ActivityIndicator />
        ) : (
          <DataTable
            key="patron-sweepstakes-table"
            sortFieldName="sweepstakeId"
            sort="asc"
            columns={patronSweepstakeColumns}
            rows={patronSweepstakeRows}
            sx={{
              '& .MuiDataGrid-cell': {
                color: colorPrimitives.black,
              },
            }}
          />
        )}
      </Box>
    </PatronDetailsHeader>
  )
}
